import TableElement from '@/table/TableElement';
import {
  Box,
  Button,
  Table,
  TableContainer,
  Tbody,
  Text
} from '@chakra-ui/react';
import { AiOutlineFile } from 'react-icons/ai';
import { InputField } from './input';
import { Path } from 'react-hook-form';
import { useState } from 'react';
import { formatPhoneNumber } from '@/explore/ffl-detail/utils.mjs';
import {
  ProfileFormInterface,
  UseFormNameInterface
} from '@/model/profile.mjs';
import { emailDomain } from '@/model/Constants';
import { BUSINESS_NAME } from '@/utils/constants';
import { getUrl } from 'aws-amplify/storage';

export const ProfileForm = ({
  isEditEnabled,
  profileData,
  setProfileData,
  setFile,
  file,
  register,
  errors,
  callUploadApi
}: ProfileFormInterface) => {
  const [downloadingFile, setDownloadingFile] = useState<boolean>(false);

  async function getFile() {
    setDownloadingFile(true);
    try {
      let fileUrl: any = profileData.store_license_file_path? await getUrl({
        key: profileData.store_license_file_path,
        options: {
          bucket: import.meta.env.VITE_LICENSE_READER_BUCKET,
          accessLevel: 'guest'
        },
      }) : null
      fileUrl = fileUrl?.url
      const link = document.createElement('a');
      link.href = URL.createObjectURL(await (await fetch(fileUrl)).blob());
      link.download = 'license';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadingFile(false);
    } catch (e: any) {
      setDownloadingFile(false);
      console.error('Error downloading file:', String(e));
    }
  }
  function renderData(data: string, name: Path<UseFormNameInterface>) {
    if (isEditEnabled) {
      if (name === 'store_license_file_path')
        return (
          <>
            <Button position="relative">
              Upload
              <input
                className="file-upload"
                type="file"
                onChange={(e) => {
                  setFile(e.target.files);
                }}
              />
            </Button>{' '}
            {file && file[0].name}
          </>
        );

      return (
        <>
          <InputField
            detail={data}
            name={name}
            register={register}
            setProfileData={setProfileData}
            profileData={profileData}
          />
        </>
      );
    } else {
      if (name === 'store_license_file_path') {
        return (
          <>
            {profileData.store_license_file_path ? (
              <Button
                variant={'unstyled'}
                isLoading={downloadingFile || callUploadApi}
                style={{ display: 'Flex' }}
                onClick={() => getFile()}
              >
                <AiOutlineFile />
              </Button>
            ) : (
              '-'
            )}
          </>
        );
      }else if (name === 'seller_email') return data ? `${data}${emailDomain}` : ''
      return data;
    }
  }
  function dataFormatter(data: string | null | undefined) {
    if (data) return data;
    return '';
  }

  return (
    <>
      <Box className={'ProfileForm'}>
        <div className="transfer-details-content">
          <TableContainer marginBottom={'2rem'} flex={1}>
            <Table>
              <Tbody>
                <TableElement label={BUSINESS_NAME}>
                  {renderData(dataFormatter(profileData.name), 'name')}
                  {isEditEnabled && errors.name && (
                    <Text as="sub" color="red">
                      {errors.name.message}
                    </Text>
                  )}
                </TableElement>
                <TableElement label={'Mailing Address'}>
                  <Box>
                    {renderData(
                      dataFormatter(profileData.store_premise_street),
                      'store_premise_street'
                    )}
                    {isEditEnabled && errors.store_premise_street && (
                      <Text as="sub" color="red">
                        {errors.store_premise_street.message}
                      </Text>
                    )}
                  </Box>
                  <Box>
                    {renderData(
                      dataFormatter(profileData.store_premise_city),
                      'store_premise_city'
                    )}
                    {isEditEnabled && errors.store_premise_city && (
                      <Text as="sub" color="red">
                        {errors.store_premise_city.message}
                      </Text>
                    )}
                  </Box>
                  <Box>
                    {renderData(
                      dataFormatter(profileData.store_premise_state),
                      'store_premise_state'
                    )}
                    {isEditEnabled && errors.store_premise_state && (
                      <Text as="sub" color="red">
                        {errors.store_premise_state.message}
                      </Text>
                    )}
                  </Box>
                  <Box>
                    {renderData(
                      dataFormatter(profileData.store_premise_zip_code),
                      'store_premise_zip_code'
                    )}
                    {isEditEnabled && errors.store_premise_zip_code && (
                      <Text as="sub" color="red">
                        {errors.store_premise_zip_code.message}
                      </Text>
                    )}
                  </Box>
                </TableElement>
                <TableElement label={'Contact'}>
                  {!isEditEnabled
                    ? formatPhoneNumber(
                        dataFormatter(profileData.contact_phone)
                      )
                    : renderData(
                        dataFormatter(profileData.contact_phone),
                        'contact_phone'
                      )}
                  {isEditEnabled && errors.contact_phone && (
                    <Text as="sub" color="red">
                      {errors.contact_phone.message}
                    </Text>
                  )}
                </TableElement>
                <TableElement label={'Fax'}>
                  {!isEditEnabled
                    ? formatPhoneNumber(
                        dataFormatter(profileData.store_contact_fax)
                      )
                    : renderData(
                        dataFormatter(profileData.store_contact_fax),
                        'store_contact_fax'
                      )}
                  {isEditEnabled && errors.store_contact_fax && (
                    <Text as="sub" color="red">
                      {errors.store_contact_fax.message}
                    </Text>
                  )}
                </TableElement>
                <TableElement label={'Contact Email'}>
                  {renderData(
                    dataFormatter(profileData.contact_email),
                    'contact_email'
                  )}
                  {isEditEnabled && errors.contact_email && (
                    <Text as="sub" color="red">
                      {errors.contact_email.message}
                    </Text>
                  )}
                </TableElement>
              </Tbody>
            </Table>
          </TableContainer>
          <TableContainer marginBottom={'2rem'} flex={1}>
            <Table>
              <Tbody>
                <TableElement label={'Sender Email'}>
                  {renderData(
                    dataFormatter(profileData.seller_email),
                    'seller_email'
                  )}
                  {isEditEnabled && errors.seller_email && (
                    <Text as="sub" color="red">
                      {errors.seller_email.message}
                    </Text>
                  )}
                </TableElement>
                <TableElement label={'Store ID'}>
                  {dataFormatter(profileData.store_id)}
                </TableElement>
                <TableElement label={'License ID'} verticalAlign={'middle'}>
                  {dataFormatter(profileData.store_license_key)}
                </TableElement>
                <TableElement label={'License Type'}>
                  {dataFormatter(profileData.store_lic_type)}
                </TableElement>
                <TableElement label={'License Certificate'}>
                  {renderData(
                    dataFormatter(profileData.store_license_file_path),
                    'store_license_file_path'
                  )}
                </TableElement>
              </Tbody>
            </Table>
          </TableContainer>
        </div>
      </Box>
    </>
  );
};
